type Subscriber<T> = (state: T) => any;
type PreSet<T> = (state: T, newState: T) => any;
type PrePatch<T> = (state: T, partialState: Partial<T>) => any;

class Store<T extends {} | undefined> {
	private stateOriginal: T;
	private state: T;
	private subscribers: Subscriber<T>[] = [];
	private preSet: PreSet<T> | null = null;
	private prePatch: PrePatch<T> | null = null;

	constructor(initialState: T) {
		this.state = this.deepClone(initialState);
		this.stateOriginal = this.deepClone(initialState);
	}
	public reset() {
		this.state = this.deepClone(this.stateOriginal);
		const mState = this.get();
		this.subscribers.forEach(cb => cb(mState));
	}

	private deepClone(ob: T): T {
		try {
			return window.structuredClone != null ? window.structuredClone(ob) : JSON.parse(JSON.stringify(ob));
		} catch (e) {
			return JSON.parse(JSON.stringify(ob));
		}
	}

	async set(newState: T) {
		if (this.preSet) {
			await this.preSet(this.state, newState);
		}
		this.state = newState;
		const mState = this.get();
		this.subscribers.forEach(cb => cb(mState));
	}

	async patch(partialState: T extends object ? Partial<T> : T) {
		if (this.prePatch) {
			await this.prePatch(this.state, partialState);
		}
		if (this.state == null) {
			this.state = partialState as T;
		} else if (typeof partialState === "object" && partialState !== null) {
			Object.assign(this.state, partialState);
		} else {
			// partialState is undefined
			this.state = partialState as T;
		}
		const mState = this.get();
		this.subscribers.forEach(cb => cb(mState));
	}

	/**
	 * @deprecated Use `patch` instead
	 */
	dispatch(partialState: T extends object ? Partial<T> : T) {
		this.patch(partialState);
	}

	get() {
		return this.state ? this.deepClone(this.state) : (undefined as T);
	}

	getState() {
		return this.get();
	}

	subscribe(callback: Subscriber<T>) {
		this.subscribers.push(callback);
		return () => {
			this.subscribers = this.subscribers.filter(cb => cb !== callback);
		};
	}

	setOnPreSet(cb: PreSet<T>) {
		this.preSet = cb;
	}

	setOnPrePatch(cb: PrePatch<T>) {
		this.prePatch = cb;
	}
}

export function createStore<T extends {} | undefined>({ initialState, id }: { initialState: T; id?: string }) {
	const store = new Store<T>(initialState);

	// @ts-ignore
	window.cnxStore = window.cnxStore || {};
	// @ts-ignore
	window.cnxStore[id] = store;

	return store;
}

type Constructor<T = {}> = new (...args: any[]) => T;
interface CustomElement {
	connectedCallback?(): void;
	disconnectedCallback?(): void;
	_storeUnsubscribe?: () => void;
	readonly isConnected: boolean;
}

export const connect =
	<S extends {}>(store: Store<S>) =>
	<T extends Constructor<CustomElement>>(baseElement: T) =>
		class extends baseElement {
			connectedCallback() {
				super.connectedCallback?.();
				this._storeUnsubscribe = store.subscribe(() => this.stateChanged(store.getState()));
				this.stateChanged(store.getState());
			}

			disconnectedCallback() {
				if (this._storeUnsubscribe) {
					this._storeUnsubscribe();
				}
				super.disconnectedCallback?.();
			}

			stateChanged(_state: S) {}
		};
